import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@shared/constants/languages.ts';
import type { LocalizedText } from '@shared/constants/localized-text.ts';
import { TextArea, TextInput } from './ui.tsx';
import { useContentLanguage } from '../i18n/localized.ts';

/**
 * The second-language half of a room type or task in the library editor.
 *
 * The field above this one — "Room type name" — is the company's own wording,
 * in the language it sends in; it is what a customer's quote, email and PDF
 * print. This block is what a member of staff reading the app in another
 * language sees instead (see localized-text.ts), and it is deliberately drawn *below* and
 * *labeled* rather than merged into a language picker: an owner filling in
 * their library should not have to work out which language they are currently
 * typing the customer-facing name in.
 *
 * Only languages other than the company's own appear. The company's own is
 * already the field above, and showing it twice would let the two disagree.
 */

/** A draft field that has a per-language counterpart. */
export interface TranslatableField {
  /** Stable key, only used to key the rendered controls. */
  key: string;
  /** The field's label, already translated, with no language in it. */
  label: string;
  /** Multi-line, for a description. */
  multiline?: boolean;
  rows?: number;
  /** The draft's current text for one language. */
  valueFor: (language: SupportedLanguage) => string;
  onChange: (language: SupportedLanguage, value: string) => void;
}

/**
 * A language's name, written in the language the reader is reading.
 *
 * `Intl.DisplayNames` rather than a hand-kept table: the app already ships
 * `Intl` for dates and money, and a table would need an entry per language per
 * language — the thing that goes stale first. Falls back to the raw tag on a
 * runtime without it, which is a worse label but never a missing one.
 */
function languageName(tag: SupportedLanguage, readingIn: SupportedLanguage): string {
  try {
    return new Intl.DisplayNames([readingIn], { type: 'language' }).of(tag) ?? tag;
  } catch {
    return tag;
  }
}

interface LocalizedFieldsProps {
  /** The language the fields above are written in — the company's own. */
  baseLanguage: SupportedLanguage;
  fields: TranslatableField[];
}

export default function LocalizedFields({ baseLanguage, fields }: LocalizedFieldsProps) {
  const { t } = useTranslation();
  const readingIn = useContentLanguage();
  const others = SUPPORTED_LANGUAGES.filter((language) => language !== baseLanguage);
  if (others.length === 0 || fields.length === 0) return null;

  return (
    <div className="mt-4 rounded-md border border-[color:var(--color-line)] bg-[color:var(--color-surface)] p-4">
      <h4 className="text-sm font-semibold">{t('localizedContent.heading')}</h4>
      <p className="field-hint mb-3">
        {t('localizedContent.hint', { language: languageName(baseLanguage, readingIn) })}
      </p>
      {others.map((language) => (
        <div key={language}>
          {fields.map((field) =>
            field.multiline ? (
              <TextArea
                key={`${field.key}-${language}`}
                label={t('localizedContent.fieldLabel', {
                  field: field.label,
                  language: languageName(language, readingIn),
                })}
                rows={field.rows ?? 2}
                value={field.valueFor(language)}
                onChange={(event) => field.onChange(language, event.target.value)}
              />
            ) : (
              <TextInput
                key={`${field.key}-${language}`}
                label={t('localizedContent.fieldLabel', {
                  field: field.label,
                  language: languageName(language, readingIn),
                })}
                hint={t('localizedContent.blankHint', { language: languageName(baseLanguage, readingIn) })}
                value={field.valueFor(language)}
                onChange={(event) => field.onChange(language, event.target.value)}
              />
            ),
          )}
        </div>
      ))}
    </div>
  );
}

/**
 * The draft shape these fields edit: one string per language, blanks included.
 *
 * A `Record` with an entry for every language rather than a `LocalizedText`,
 * because a half-typed and then cleared box has to stay cleared while the form
 * is open — `LocalizedText` drops blanks, which would make the box refill from
 * whatever was there before on the next render.
 */
export type TranslationDraft = Record<SupportedLanguage, string>;

export function emptyTranslationDraft(): TranslationDraft {
  return Object.fromEntries(SUPPORTED_LANGUAGES.map((language) => [language, ''])) as TranslationDraft;
}

/** Fills a draft from a stored map, leaving unfilled languages blank. */
export function translationDraftFrom(stored: LocalizedText | null | undefined): TranslationDraft {
  return Object.fromEntries(
    SUPPORTED_LANGUAGES.map((language) => [language, stored?.[language] ?? '']),
  ) as TranslationDraft;
}

/**
 * The map to send, with the base language's entry taken from the base field.
 *
 * That last part is the invariant the whole resolution rests on: the map wins
 * over the base column wherever it has an entry, so a map whose `en` still
 * said "Kitchen" after the name was changed to "Main kitchen" would show every
 * English reader the old name while the customer's quote showed the new one.
 * Writing the base text in on every save keeps the two from ever disagreeing.
 */
export function translationPayload(
  draft: TranslationDraft,
  baseLanguage: SupportedLanguage,
  baseText: string,
): LocalizedText | null {
  const payload: LocalizedText = {};
  let translated = false;
  for (const language of SUPPORTED_LANGUAGES) {
    const text = (language === baseLanguage ? baseText : draft[language]).trim();
    if (!text) continue;
    payload[language] = text;
    if (language !== baseLanguage) translated = true;
  }
  // Nothing filled in but the base language means nothing has been translated,
  // and a map holding only what the base column already holds is noise in the
  // row. Counted rather than sized, because the base text itself can be empty
  // — a description nobody wrote — and a map of one Spanish description is a
  // translation, not noise.
  return translated ? payload : null;
}
